import React from "react";
import {
  Button,
  Row,
  Col,
  CardBody,
  Collapse,
  Card,
  Input,
  Table
} from "reactstrap";

// Images
import ic_arrow_down from "ASSETS/Images/ic_arrow_down.svg";
import ic_edit from "ASSETS/Images/qr_icons/ic_edit.webp";
import ic_add from "ASSETS/Images/ic_add_storage.svg";
import ic_delete from "ASSETS/Images/qr_icons/ic_delete.webp";
import Switch from "COMPONENTS/CommonComponents/Switch/Switch";

import { getMessage } from "CONFIG/i18n";

class UIGuestSettingsSection extends React.Component { 

  constructor(props) {
    super(props);
    this.state = {
      // Set Text state
      newSetText: '',
      newSetTextValue: '',
      newSetTextLanguage: 'ja',
      enableSetTextEdit: false,
      setTextToEdit: '',
      setTextValueToEdit: '',
      setTextLanguageToEdit: 'ja',
      setTextEdit: '',
      setTextValueEdit: '',
      setTextIndexToEdit: null,
    };
  }

  handleSetTextInputChange = (event) => {
    const newTxt = event.target.value;
    const fieldName = event.target.name;
    
    // Allow any text for Set Text functionality
    if (this.state.enableSetTextEdit) {
      if (fieldName === 'setTextToEdit') {
        this.setState({
          setTextToEdit: newTxt
        });
      } else if (fieldName === 'setTextValueToEdit') {
        this.setState({
          setTextValueToEdit: newTxt
        });
      } else if (fieldName === 'setTextLanguageToEdit') {
        this.setState({
          setTextLanguageToEdit: newTxt
        });
      }
    } 
    else {
      if (fieldName === 'newSetText') {
        this.setState({ newSetText: newTxt });
      } else if (fieldName === 'newSetTextValue') {
        this.setState({ newSetTextValue: newTxt });
      } else if (fieldName === 'newSetTextLanguage') {
        this.setState({ newSetTextLanguage: newTxt });
      }
    }
  };

  handleSetTextPaste = (event) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const fieldName = event.target.name;
    
    // Allow any text for Set Text functionality
    if (this.state.enableSetTextEdit) {
      if (fieldName === 'setTextToEdit') {
        this.setState({
          setTextToEdit: pastedText
        });
      } else if (fieldName === 'setTextValueToEdit') {
        this.setState({
          setTextValueToEdit: pastedText
        });
      }
    } else {
      if (fieldName === 'newSetText') {
        this.setState({ newSetText: pastedText });
      } else if (fieldName === 'newSetTextValue') {
        this.setState({ newSetTextValue: pastedText });
      }
    }
  };

  // Set Text methods
  handleAddSetText = () => {
    const setTextObject = {
      language: this.state.newSetTextLanguage,
      text: this.state.newSetText,
      value: this.state.newSetTextValue
    };
  
    this.props.addSetText(setTextObject);
    this.setState({
      newSetText: '',
      newSetTextValue: '',
      newSetTextLanguage: 'ja'
    }, this.toggleSetTextVisibility);
  };

  handleEditSetText = () => {
    const setTextObject = {
      language: this.state.setTextLanguageToEdit,
      text: this.state.setTextToEdit,
      value: this.state.setTextValueToEdit
    };
    this.props.editSetText(this.state.setTextIndexToEdit, setTextObject);
    this.setState({
      newSetText: '',
      newSetTextValue: '',
      newSetTextLanguage: 'ja'
    }, this.toggleSetTextVisibility);
  }

  toggleSetTextVisibility = () => {
    const newIsShown = !this.props.showSetTextAdd;
    this.props.onToggleSetText(newIsShown);
    this.setState({
      enableSetTextEdit: false,
      setTextToEdit: '',
      setTextValueToEdit: '',
      setTextEdit: '',
      setTextValueEdit: '',
    });
  };

  toggleSetTextEdit = (index) => {
    this.toggleSetTextVisibility();
    const item = this.props.setTexts[index];
    this.setState({
      enableSetTextEdit: true,
      setTextIndexToEdit: index,
      setTextToEdit: typeof item === 'string' ? item : item.text || '',
      setTextValueToEdit: typeof item === 'string' ? '' : item.value || '',
      setTextLanguageToEdit: typeof item === 'object' && item.language ? item.language : 'ja',
      setTextEdit: typeof item === 'string' ? item : item.text || '',
      setTextValueEdit: typeof item === 'string' ? '' : item.value || '',
    });
  }

  /**
   * Converts a text item into a standardized language and text object.
   * @param {string|Object} item - The input item. Can be a simple string or an object with language and text properties.
   * @param {string} [item.language] - The language code (defaults to 'ja' if not provided).
   * @param {string} [item.text] - The text content (defaults to empty string if not provided).
   */
  getSetTextLanguageAndText = (item) => {
    if (typeof item === 'string') return { language: 'ja', text: item };
    return { language: item.language || 'ja', text: item.text || '' };
  };

  /**
   * Normalizes text input by converting it to a standard format.
   * 
   * @param {string|null|undefined} value - The input value to normalize
   * @returns {string} The normalized string in lowercase, trimmed, and in NFKC form
   */
  normalizeSetTextInput = (value) =>
    (value ?? '').toString().normalize('NFKC').trim().toLowerCase();

  /**
   * Builds a text key by combining a language code with normalized text input.
   * @param {string} language - The language code to prepend to the key.
   * @param {string} text - The text input to normalize and append to the key.
   * @returns {string} A pipe-delimited string in the format "language|normalizedText".
   */
  buildSetTextKey = (language, text) =>
    `${language}|${this.normalizeSetTextInput(text)}`;

  /**
   * Checks if a set text with the given language and text already exists in the list.
   * @param {string} language - The language code of the set text to check.
   * @param {string} text - The text content to check for duplicates.
   * @param {number|null} [excludeIndex=null] - The index of an item to exclude from the duplicate check (useful when updating an existing item).
   * @returns {boolean} True if a duplicate set text exists, false otherwise.
   */
  isDuplicateSetTextFor = (language, text, excludeIndex = null) => {
    const list = this.props.setTexts || [];
    const proposedKey = this.buildSetTextKey(language, text);
    return list.some((item, index) => {
      if (excludeIndex !== null && index === excludeIndex) return false;
      const { language: itemLanguage, text: itemText } = this.getSetTextLanguageAndText(item);
      return this.buildSetTextKey(itemLanguage, itemText) === proposedKey;
    });
  };

  /**
   * Checks if the set text is a duplicate based on the current edit state.
   * 
   * If text editing is enabled, validates the text being edited against existing entries.
   * Otherwise, validates the newly created text against existing entries.
   * 
   * @returns {boolean} True if the set text is a duplicate, false otherwise.
   */
  checkCurrentFormForDuplicate = () => {
    if (this.state.enableSetTextEdit) {
      return this.isDuplicateSetTextFor(
        this.state.setTextLanguageToEdit,
        this.state.setTextToEdit,
        this.state.setTextIndexToEdit
      );
    }
    return this.isDuplicateSetTextFor(
      this.state.newSetTextLanguage,
      this.state.newSetText
    );
  };

  // Validation helper for Set Text form
  isSetTextFormValid = () => {
    if (this.state.enableSetTextEdit) {
      const currentItem = this.props.setTexts[this.state.setTextIndexToEdit];
      const currentText = typeof currentItem === 'string' ? currentItem : (currentItem?.text || '');
      const currentValue = typeof currentItem === 'string' ? '' : (currentItem?.value || '');
      const currentLanguage = typeof currentItem === 'object' && currentItem.language ? currentItem.language : 'en';
      const changed = (
        this.state.setTextToEdit !== currentText ||
        this.state.setTextValueToEdit !== currentValue ||
        this.state.setTextLanguageToEdit !== currentLanguage
      );
      const notEmpty = (
        this.state.setTextToEdit.trim() !== '' &&
        this.state.setTextValueToEdit.trim() !== '' &&
        this.state.setTextLanguageToEdit.trim() !== ''
      );
      const isDuplicate = this.isDuplicateSetTextFor(
        this.state.setTextLanguageToEdit,
        this.state.setTextToEdit,
        this.state.setTextIndexToEdit
      );
      return changed && notEmpty && !isDuplicate;
    } else {
      const notEmpty = (
        this.state.newSetText.trim() !== '' &&
        this.state.newSetTextValue.trim() !== '' &&
        this.state.newSetTextLanguage.trim() !== ''
      );
      const isDuplicate = this.isDuplicateSetTextFor(
        this.state.newSetTextLanguage,
        this.state.newSetText
      );
      return notEmpty && !isDuplicate;
    }
  };

  render() {
    return (
      <div className="section">
        <Row className="noMargin">
          <Col md={10} className="mainTitle">
            {getMessage("UI_SETTINGS_GUEST")}
          </Col>
          <Col>
            <img
              style={{ transform: `rotate(${this.props.uiGuestSettingsRotation}deg)` }}
              className="dropDown cursorPointer floatRight"
              src={ic_arrow_down}
              onClick={() => this.props.handleUIGuestSettingsArrowClick()} />
          </Col> 
        </Row>
        <Collapse isOpen={this.props.isUIGuestSettingsToggler}>
          {/* Set Text Section */}
          <Card className="mt-3 borderlessCard">
            <CardBody>
              <Row className="rowPadding">
                <Col xs={2} className="colInboundCobrowse">
                  <Switch
                    switchOn={this.props.setTextEnabled}
                    toggleSwitch={(event)=>{this.props.toggleSetTextSwitch(event)}}
                  />
                </Col>
                <Col xs={8} className="textOverflow">
                  {getMessage("ENABLE_SET_TEXT")}
                </Col>
                <Col xs={2} className="colInboundCobrowseRight">
                  <img src={ic_add}
                    onClick={this.toggleSetTextVisibility}
                    title={getMessage("ADD") + "/" + getMessage("EDIT") + String.fromCharCode(160) + getMessage("SET_TEXT")}
                  />
                </Col>
              </Row>
                  
              <Row className="rowPadding">
                <Table className="tableInboundCoBrowseSetText">
                  <thead>
                    <tr>
                      <th className="num">{getMessage("SERIAL_NUMBER")}</th>
                      <th className="lang">{getMessage("LANGUAGE")}</th>
                      <th>{getMessage("SET_TEXT")}</th>
                      <th className="main">{getMessage("SET_TEXT_VALUE")}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {this.props.setTexts && this.props.setTexts.map((item, index) =>
                      <tr key={(typeof item === 'string' ? item : item.text) + index}>
                        <td>{index + 1}</td>
                        <td>{typeof item === 'object' && item.language ? item.language : ''}</td>
                        <td>{typeof item === 'string' ? item : item.text || ''}</td>
                        <td style={{ textAlign: 'left' }}>{typeof item === 'string' ? '' : item.value || ''}</td>
                        <td>
                          <img src={ic_edit} alt="Edit" className="cursorPointer px-2"
                            onClick={() => {this.toggleSetTextEdit(index)}}
                          />
                          <img
                            src={ic_delete} alt="Delete" className="cursorPointer px-2"
                            onClick={() => this.props.handleDeleteSetText(index)}
                          />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Row>
            </CardBody>

            <Card className="cobrowseCard inboundCobrowseCard">
              {this.props.showSetTextAdd && 
                  <CardBody>
                    <Row className="rowPadding">
                      <Col className="colInboundCobrowse">
                        {this.state.enableSetTextEdit ? (
                          getMessage("SET_TEXT") + String.fromCharCode(160) + "(" + getMessage("EDIT") + "): " + this.state.setTextEdit
                        ) : (
                          getMessage("SET_TEXT") + String.fromCharCode(160) + "(" + getMessage("ADD") + ")"
                        )}
                      </Col>
                      <Col>
                        <Button close aria-label="Cancel" onClick={this.toggleSetTextVisibility} type="button"/>
                      </Col>
                    </Row>
            
                    <Row className="rowPadding">
                      <Col xs={3} className="colInboundCobrowse">{getMessage("LANGUAGE")} :</Col>
                      <Col xs={9}>
                        <Input
                          type="select"
                          name={this.state.enableSetTextEdit ? "setTextLanguageToEdit" : "newSetTextLanguage"}
                          className="inputFormControl"
                          onChange={this.handleSetTextInputChange}
                          value={this.state.enableSetTextEdit ? this.state.setTextLanguageToEdit : this.state.newSetTextLanguage}
                        >
                          {/* sequence change * JA must be first in the selection:  配列変更 * JAは選択で最初に指定されなければならない */}
                          <option value="ja">ja</option>
                          <option value="en">en</option>
                        </Input>
                      </Col>
                    </Row>
                    <Row className="rowPadding">
                      <Col xs={3} className="colInboundCobrowse">{getMessage("SET_TEXT")} :</Col>
                      <Col xs={9}>
                        <Input
                          type="text"
                          name={this.state.enableSetTextEdit ? "setTextToEdit" : "newSetText"}
                          className="inputFormControl"
                          onChange={this.handleSetTextInputChange}
                          onPaste={this.handleSetTextPaste}
                          value={this.state.enableSetTextEdit ? (this.state.setTextToEdit) : (this.state.newSetText)}
                        />
                        {this.checkCurrentFormForDuplicate() && (
                          <div className="text-danger mt-1 small center-text">
                            {getMessage("DUPLICATE_SET_TEXT", {
                              language: this.state.enableSetTextEdit ? this.state.setTextLanguageToEdit : this.state.newSetTextLanguage,
                              text: this.state.enableSetTextEdit ? this.state.setTextToEdit : this.state.newSetText
                            })}
                          </div>
                        )}
                      </Col> 
                    </Row>
                    <Row className="rowPadding">
                      <Col xs={3} className="colInboundCobrowse">{getMessage("SET_TEXT_VALUE")} :</Col>
                      <Col xs={9}>
                        <Input
                          type="text"
                          name={this.state.enableSetTextEdit ? "setTextValueToEdit" : "newSetTextValue"}
                          className="inputFormControl"
                          onChange={this.handleSetTextInputChange}
                          onPaste={this.handleSetTextPaste}
                          value={this.state.enableSetTextEdit ? (this.state.setTextValueToEdit) : (this.state.newSetTextValue)}
                        />  
                      </Col> 
                    </Row>
                    <Row className="interCardContent floatRight mx-2">
                      <Button
                        className={
                          !this.isSetTextFormValid()
                            ? "disabledBtn ml-3 mt-2"
                            : "borderBtn cursorPointer bgwhite ml-3 mt-2"
                        }
                        disabled={!this.isSetTextFormValid()}
                        style={
                          !this.isSetTextFormValid()
                            ? { cursor: 'no-drop', pointerEvents: 'auto' }
                            : { cursor: 'pointer' }
                        }
                        onClick={this.state.enableSetTextEdit ? this.handleEditSetText : this.handleAddSetText}
                      >
                        {this.state.enableSetTextEdit ? (getMessage("EDIT")) : (getMessage("ADD"))}
                      </Button>
                    </Row>
                  </CardBody>
              }
            </Card>
          </Card>
        
        </Collapse>
      </div>
    )
  }
}
export default UIGuestSettingsSection;
